import { Action, Dispatch } from 'redux';
import { IAppState } from '../reducers';
import { IVaultTeam } from '../../api/vault/teams';
import {
  IVaultSubscription,
  getVaultSubscriptions,
  createVaultSubscription,
  updateVaultSubscription,
} from '../../api/vault/subscriptions';
import { selectIsUserExchangeVendor, selectUserOrgIds } from '../../state/user/userOrgRole';
import { ActionType as ValidationAction } from '../validation';
import { Validation } from '../../constants/validation';
import { validationReducer } from '../../utils/validation';
import { getVendorTeams } from './vendorData';

export enum ValidationKey {
  Email = 'NotificationSettingsEmail',
  Phone = 'NotificationSettingsPhone',
}

export const ValidationRules = {
  Email: [Validation.NOT_EMPTY, Validation.EMAIL],
};

export interface IState {
  isOpen: boolean;
  isLoading: boolean;
  isLoaded: boolean;
  subscription: IVaultSubscription;
  editedEmail: string;
  editedPhoneNumber: string;
  teams: Array<IVaultTeam>;
  teamFilter: string;
}

export enum ActionType {
  Toggle = 'ExchangeNotificationSettings.Toggle',
  StartLoading = 'ExchangeNotificationSettings.StartLoading',
  FinishLoading = 'ExchangeNotificationSettings.FinishLoading',
  UpdateSettings = 'ExchangeNotificationSettings.UpdateSettings',
  UpdateEmail = 'ExchangeNotificationSettings.UpdateEmail',
  UpdatePhoneNumber = 'ExchangeNotificationSettings.UpdatePhoneNumber',
  AddEmail = 'ExchangeNotificationSettings.AddEmail',
  AddPhoneNumber = 'ExchangeNotificationSettings.AddPhoneNumber',
  RemoveEmail = 'ExchangeNotificationSettings.RemoveEmail',
  RemovePhoneNumber = 'ExchangeNotificationSettings.RemovePhoneNumber',
  UpdateTeams = 'ExchangeNotificationSettings.UpdateTeams',
  UpdateTeamFilter = 'ExchangeNotificationSettings.TeamFilter',
  Clear = 'ExchangeNotificationSettings.Clear',
}
export interface IExchangeNotificationSettingsAction extends Action<ActionType> {
  payload?: any,
}

const INITIAL_STATE: IState = {
  isOpen: false,
  isLoading: false,
  isLoaded: false,
  subscription: {
    orgId: -1,
    emailAddresses: [],
    phoneNumbers: [],
    subscribedTeams: [],
    isPushToCloud: false,
    isFirstAvailableOnly: false,
  },
  editedEmail: '',
  editedPhoneNumber: '',
  teams: [],
  teamFilter: '',
};

export default function reducer(state: IState = INITIAL_STATE, action: IExchangeNotificationSettingsAction): IState {
  switch (action.type) {
    case ActionType.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ActionType.StartLoading:
      return {
        ...state,
        isLoading: true,
      };
    case ActionType.FinishLoading:
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
      };
    case ActionType.UpdateSettings:
      return {
        ...state,
        subscription: action.payload.subscription,
      };
    case ActionType.UpdateEmail:
      return {
        ...state,
        editedEmail: action.payload.email,
      };
    case ActionType.UpdatePhoneNumber:
      return {
        ...state,
        editedPhoneNumber: action.payload.phoneNumber,
      };
    case ActionType.AddEmail:
      return {
        ...state,
        subscription: {
          ...state.subscription,
          emailAddresses: [
            ...state.subscription.emailAddresses,
            action.payload.email,
          ],
        },
      };
    case ActionType.AddPhoneNumber:
      return {
        ...state,
        subscription: {
          ...state.subscription,
          phoneNumbers: [
            ...state.subscription.phoneNumbers,
            action.payload.phoneNumber,
          ],
        },
      };
    case ActionType.RemoveEmail:
      return {
        ...state,
        subscription: {
          ...state.subscription,
          emailAddresses: state.subscription.emailAddresses
            .filter((_, index) => index !== action.payload.index),
        },
      };
    case ActionType.RemovePhoneNumber:
      return {
        ...state,
        subscription: {
          ...state.subscription,
          phoneNumbers: state.subscription.phoneNumbers
            .filter((_, index) => index !== action.payload.index),
        },
      };
    case ActionType.UpdateTeams:
      return {
        ...state,
        teams: action.payload.teams,
      };
    case ActionType.UpdateTeamFilter:
      return {
        ...state,
        teamFilter: action.payload.teamFilter,
      };
    case ActionType.Clear:
      return INITIAL_STATE;
    default:
      return state;
  }
}

export const toggleNotificationSettings = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ValidationAction.ClearErrors,
    key: ValidationKey.Email,
  });
  dispatch({
    type: ActionType.Toggle,
  });
};

export const clearNotificationSettings = () => ({
  type: ActionType.Clear,
});

export const queryNotificationSettings = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();

  dispatch({
    type: ActionType.StartLoading,
  });

  const userOrgIds = selectUserOrgIds(state);
  const orgId = userOrgIds[0];

  if (userOrgIds.length > 1) {
    console.warn(`[QA] User belongs to multiple orgs -> targeting "${orgId}"`);
  }

  const response = await getVaultSubscriptions();
  let subscription: IVaultSubscription = (response.data?.data || []).find((sub) => sub.orgId === orgId);
  if (!subscription) {
    const createRes = await createVaultSubscription({
      orgId,
      phoneNumbers: [],
      emailAddresses: [],
      subscribedTeams: [],
      isPushToCloud: false,
      isFirstAvailableOnly: false,
    });
    if (createRes.status !== 201) {
      console.error(`[QA]: Error occurred while creating subscription settings for org "${orgId}"`);
      return;
    }
    subscription = createRes.data.data;
  } else {
    const { subscribedTeams } = subscription;
    // VA-2385 - don't load subscribed teams without a valid teamId
    // - when/if user makes subsequent update to their subscriptions, these changes will be saved
    subscription = {
      ...subscription,
      subscribedTeams: subscribedTeams.filter(({ teamId }) => !!teamId),
    };
  }

  const teamsRes = await getVendorTeams(selectIsUserExchangeVendor(state), true);
  const teams = teamsRes.data?.data || [];

  // VA-2385 - don't load teams that do not exist
  // - when/if user makes subsequent update to their subscriptions, these changes will be saved
  const { subscribedTeams } = subscription;
  subscription = {
    ...subscription,
    subscribedTeams: subscribedTeams.filter(({ teamId }) => teams.find(({ _id }) => _id === teamId)),
  };

  dispatch({
    type: ActionType.UpdateSettings,
    payload: { subscription },
  });

  dispatch({
    type: ActionType.UpdateTeams,
    payload: {
      teams,
    },
  });

  dispatch({
    type: ActionType.FinishLoading,
  });
};

export const updateEmail = (email: string) => ({
  type: ActionType.UpdateEmail,
  payload: {
    email,
  },
});

export const updatePhoneNumber = (phoneNumber: string) => ({
  type: ActionType.UpdatePhoneNumber,
  payload: {
    phoneNumber,
  },
});

export const addEmail = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();

  const email = state.exchange.notificationSettings.editedEmail.toLowerCase();
  const errors = validationReducer(email, ValidationRules.Email);

  dispatch({
    type: ValidationAction.ToggleErrors,
    key: ValidationKey.Email,
    payload: {
      errors,
    },
  });

  if (!errors.length) {
    dispatch({
      type: ActionType.AddEmail,
      payload: {
        email,
      },
    });

    dispatch({
      type: ActionType.UpdateEmail,
      payload: {
        email: INITIAL_STATE.editedEmail,
      },
    });

    dispatch({
      type: ValidationAction.ClearErrors,
      key: ValidationKey.Email,
    });
  }
};

export const addPhoneNumber = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();

  dispatch({
    type: ActionType.AddPhoneNumber,
    payload: {
      phoneNumber: state.exchange.notificationSettings.editedPhoneNumber,
    },
  });
  dispatch({
    type: ActionType.UpdatePhoneNumber,
    payload: {
      phoneNumber: INITIAL_STATE.editedPhoneNumber,
    },
  });
};

export const removeEmail = (index: number) => ({
  type: ActionType.RemoveEmail,
  payload: {
    index,
  },
});

export const removePhoneNumber = (index: number) => ({
  type: ActionType.RemovePhoneNumber,
  payload: {
    index,
  },
});

export const updateTeamFilter = (teamFilter: string) => ({
  type: ActionType.UpdateTeamFilter,
  payload: {
    teamFilter,
  },
});

export const updateSettings = (subscription: IVaultSubscription) => ({
  type: ActionType.UpdateSettings,
  payload: {
    subscription,
  },
});

export const saveSettings = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const { exchange: { notificationSettings: { subscription } } } = state;

  const response = await updateVaultSubscription(subscription);

  if (response.status !== 200) {
    // TODO: Note response status for success should be 204
    console.error(`[QA]: Could not save subscription "${subscription._id}"`);
  }
};
